import React, { useState } from 'react'
import { ErroredInstance, LoadingInstance, isErrored, isLoading } from '@rwwa/rwwa-data-access'
import { useSelector } from 'react-redux'
import { useBiometricsStatus } from '@mobi/account/Areas/Verification/Biometrics/Hooks/useBiometricsStatus'
import { DesktopSpinner, Spinner } from '@mobi/component-library/Legacy/Spinner'
import { PageHeader } from '@mobi/component-library/Navigation/PageHeader'
import {
  getIsBiometricsEnabledOnTempPasswordChange,
  getIsKeepMeLoggedIn,
} from '@core/Areas/Login/Store/selectors'
import { ButtonBlock } from '@core/Components/Buttons/ButtonBlock'
import { MainContainer } from '@core/Components/Containers'
import { observeImmutable } from '@core/Components/HOCs'
import { connect } from '@core/Components/HOCs/connect'
import { ErrorMessage, SuccessMessage } from '@core/Components/Messages'
import {
  AccountDetails,
  ChangePasswordKey,
  ChangePasswordRepo,
  MfaChallenge,
} from '@core/Data/Account/changePassword'
import { ComplexPasswordObserve } from '../Password'
import {
  ChangePasswordContainerStyled,
  ChangePasswordTitleLabelStyled,
} from './ChangePassword.styles'
import { ChangePasswordVerificationCodePageObserve } from './ChangePasswordVerificationCodePage'
import { CurrentPassword } from './Components/CurrentPassword'
import {
  ChangePasswordStateRecord,
  ChangePasswordSubmit,
  Reset,
  UpdateCurrentPassword,
  state$ as changePasswordState$,
} from './driver'

export const ChangePassword = ({ isDesktop }: { isDesktop?: boolean }) => {
  return <ConnectChangePassword changePassword={ChangePasswordKey} isDesktop={isDesktop} />
}

export const ChangePasswordLoader = ({
  changePassword,
  isDesktop,
}: {
  changePassword: typeof LoadingInstance | typeof ErroredInstance | AccountDetails
  isDesktop?: boolean
}) => {
  if (isLoading(changePassword)) {
    return isDesktop ? <DesktopSpinner /> : <Spinner />
  }

  if (isErrored(changePassword)) {
    return <ErrorMessage>An error occurred while loading change password.</ErrorMessage>
  }

  const ChangePasswordComponent = () => {
    React.useEffect(() => {
      return () => Reset()
    })
    const props = { accountNumber: changePassword.accountNumber as string }
    return <ChangePasswordViewObserve {...props} isDesktop={isDesktop} />
  }

  return <ChangePasswordComponent />
}

const ConnectChangePassword = connect({ changePassword: ChangePasswordRepo })<{
  isDesktop?: boolean
}>(ChangePasswordLoader)

interface ChangePasswordViewProps {
  isDesktop?: boolean
  isChangeTemporaryPassword?: boolean
  accountNumber: string
  isChangePasswordSuccess: boolean
  isChangePasswordSubmitEnabled: boolean
  validationError: string | false
}

export const ChangePasswordViewObserve = observeImmutable<
  ChangePasswordStateRecord,
  { accountNumber: string; isDesktop?: boolean; isChangeTemporaryPassword?: boolean }
>(changePasswordState$, ({ record, ...props }) => {
  const { isChangePasswordSuccess, isChangePasswordSubmitEnabled, validationError } = record

  return (
    <ChangePasswordView
      {...props}
      isChangePasswordSuccess={isChangePasswordSuccess}
      isChangePasswordSubmitEnabled={isChangePasswordSubmitEnabled}
      validationError={validationError}
    />
  )
})

let changePasswordForm: HTMLFormElement

export const ChangePasswordView = ({
  isDesktop,
  accountNumber,
  isChangeTemporaryPassword,
  isChangePasswordSuccess,
  isChangePasswordSubmitEnabled,
  validationError,
}: ChangePasswordViewProps) => {
  const { isBiometricsEnabled } = useBiometricsStatus()
  const isBiometricsEnabledOnTempPasswordChange = useSelector(
    getIsBiometricsEnabledOnTempPasswordChange
  )
  const isKeepMeLoggedIn = useSelector(getIsKeepMeLoggedIn)
  const [mfaChallenge, setMfaChallenge] = useState<MfaChallenge>()

  const onChangeCurrent = (event: React.FormEvent<HTMLInputElement>) => {
    UpdateCurrentPassword(event.currentTarget.value)
  }
  const changePasswordSubmit = () =>
    ChangePasswordSubmit({
      accountNumber,
      form: changePasswordForm,
      isBiometricsEnabledByUser: isBiometricsEnabled || isBiometricsEnabledOnTempPasswordChange,
      isDesktop,
      isChangeTemporaryPassword,
      onMfaChallengeReceived: setMfaChallenge,
    })
  const onFormClick = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
  }

  if (mfaChallenge && !isDesktop) {
    return (
      <MainContainer data-tid-changepassword-mfa-container=''>
        <ChangePasswordVerificationCodePageObserve
          accountNumber={accountNumber}
          mfaChallenge={mfaChallenge}
          isBiometricsEnabled={isBiometricsEnabled || isBiometricsEnabledOnTempPasswordChange}
          isChangeTemporaryPassword={isChangeTemporaryPassword}
          isKeepMeLoggedIn={isKeepMeLoggedIn}
          form={changePasswordForm}
          onMfaChallengeReceived={setMfaChallenge}
          onMfaSuccessful={() => setMfaChallenge(undefined)}
        />
      </MainContainer>
    )
  }

  return (
    <MainContainer data-tid-changepassword-main-container=''>
      {!isDesktop && <PageHeader title='Change Password' />}
      {isChangePasswordSuccess ? (
        <SuccessMessage>Successfully changed password</SuccessMessage>
      ) : null}
      {!isChangePasswordSuccess && validationError ? (
        <ErrorMessage>{validationError}</ErrorMessage>
      ) : null}
      <ChangePasswordContainerStyled>
        <ChangePasswordTitleLabelStyled>Account: {accountNumber}</ChangePasswordTitleLabelStyled>
        <form
          ref={(el: HTMLFormElement) => {
            changePasswordForm = el
          }}
          onClick={onFormClick}
          id='changePasswordForm'
        >
          <fieldset>
            {!isChangeTemporaryPassword && (
              <CurrentPassword labelName='Current Password*' onChange={onChangeCurrent} />
            )}
            <ComplexPasswordObserve />
          </fieldset>
          <ButtonBlock
            onClick={changePasswordSubmit}
            disabled={!isChangePasswordSubmitEnabled}
            form='changePasswordForm'
          >
            Change Password
          </ButtonBlock>
        </form>
      </ChangePasswordContainerStyled>
    </MainContainer>
  )
}
